import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import createMockVerificationEventSource from '../mock/createMockVerificationEventSource';
import { createFlowDefinition, FLOW_STATES } from '../mock/flowDefinition';
import useSimulationClock, { SIMULATION_STATUS } from './useSimulationClock';

const TERMINAL_STATES = [
    FLOW_STATES.completed,
    FLOW_STATES.warning,
    FLOW_STATES.failed,
];

// Branches run in parallel but their checks share one column, so the expanded
// branch only changes once the previous one had time to be read.
const BRANCH_FOCUS_DWELL = 2200;

const isTerminal = (state) => TERMINAL_STATES.includes(state);

const getStatesOfBranch = (nodes, nodeStates, branchId) => (
    nodes
        .filter((node) => node.role === 'stage' && node.branchId === branchId)
        .map((node) => nodeStates[node.id] || FLOW_STATES.pending)
);

const getRelationshipState = (relationship, nodeStates) => {
    const readsSource = relationship.dependencyType === 'check-result'
        || relationship.dependencyType === 'data-dependency';
    const nodeId = readsSource ? relationship.source : relationship.target;

    return nodeStates[nodeId] || FLOW_STATES.pending;
};

/**
 * Owns the verification state of the graph. The mock event source can be swapped
 * for a real-time source without touching the React Flow presentation layer.
 */
const useBidVerificationFlow = (bid) => {
    const definition = useMemo(() => createFlowDefinition(bid), [bid]);
    const eventSource = useMemo(() => createMockVerificationEventSource(bid), [bid]);
    const nodesById = useMemo(() => (
        definition.nodes.reduce((byId, node) => ({ ...byId, [node.id]: node }), {})
    ), [definition.nodes]);
    const initialNodeStates = useMemo(() => (
        definition.nodes.reduce((states, node) => ({
            ...states,
            [node.id]: node.state,
        }), {})
    ), [definition.nodes]);

    const [nodeStates, setNodeStates] = useState(initialNodeStates);
    const [liveBranchId, setLiveBranchId] = useState(null);
    const focusRef = useRef({ branchId: null, since: 0 });

    const handleAdvance = useCallback((fromTime, toTime) => {
        const events = eventSource.getEventsBetween(fromTime, toTime);

        if (!events.length) return;

        setNodeStates((currentStates) => events.reduce((states, event) => ({
            ...states,
            [event.nodeId]: event.state,
        }), currentStates));

        const startedCheck = events
            .filter((event) => (
                event.state === FLOW_STATES.processing
                && nodesById[event.nodeId]?.role === 'stage'
            ))
            .pop();

        if (!startedCheck) return;

        const { branchId } = nodesById[startedCheck.nodeId];
        const focus = focusRef.current;

        if (focus.branchId !== branchId && toTime - focus.since >= BRANCH_FOCUS_DWELL) {
            focusRef.current = { branchId, since: toTime };
            setLiveBranchId(branchId);
        }
    }, [eventSource, nodesById]);

    const {
        getTime,
        pause,
        reset: resetClock,
        start: startClock,
        status,
    } = useSimulationClock({
        duration: eventSource.duration,
        onAdvance: handleAdvance,
    });
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
        if (status !== SIMULATION_STATUS.running) {
            setElapsed(getTime());

            return undefined;
        }

        const interval = window.setInterval(() => {
            setElapsed(getTime());
        }, 200);

        return () => window.clearInterval(interval);
    }, [getTime, status]);

    const reset = useCallback(() => {
        resetClock();
        focusRef.current = { branchId: null, since: 0 };
        setElapsed(0);
        setNodeStates(initialNodeStates);
        setLiveBranchId(null);
    }, [initialNodeStates, resetClock]);

    const start = useCallback(() => {
        if (status === SIMULATION_STATUS.finished) reset();

        startClock();
    }, [reset, startClock, status]);

    useEffect(() => {
        reset();
        startClock();
    }, [reset, startClock]);

    const getEdgeTrails = useCallback(() => (
        eventSource.getEdgeTrailsAt(getTime())
    ), [eventSource, getTime]);
    const getActiveSignals = getEdgeTrails;

    const nodes = useMemo(() => {
        const branchStates = definition.nodes
            .filter((node) => node.role === 'branch')
            .map((node) => nodeStates[node.id] || FLOW_STATES.pending);

        return definition.nodes.map((node) => {
            const state = nodeStates[node.id] || FLOW_STATES.pending;

            if (node.role === 'branchStatus') {
                return {
                    ...node,
                    state: nodeStates[node.branchId] || FLOW_STATES.pending,
                };
            }

            if (node.role === 'branch') {
                const stageStates = getStatesOfBranch(definition.nodes, nodeStates, node.id);

                return {
                    ...node,
                    state,
                    completedStages: stageStates.filter(isTerminal).length,
                    hasFinding: stageStates.some((stageState) => (
                        stageState === FLOW_STATES.warning || stageState === FLOW_STATES.failed
                    )),
                };
            }

            if (node.role === 'analysis') {
                return {
                    ...node,
                    state,
                    completedBranches: branchStates.filter(isTerminal).length,
                };
            }

            return {
                ...node,
                state,
            };
        });
    }, [
        definition.nodes,
        nodeStates,
    ]);

    const relationships = useMemo(() => (
        definition.relationships.map((relationship) => ({
            ...relationship,
            state: getRelationshipState(relationship, nodeStates),
        }))
    ), [
        definition.relationships,
        nodeStates,
    ]);

    return {
        duration: eventSource.duration,
        elapsed,
        getActiveSignals,
        getEdgeTrails,
        liveBranchId,
        nodes,
        pause,
        relationships,
        reset,
        start,
        status,
    };
};

export default useBidVerificationFlow;
